import React, { useState, useEffect } from "react";
import {
    MenuItem,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    TextField,
    IconButton,
    FormControl,
    InputLabel,
    Select,
} from "@mui/material";
import { Close } from "@mui/icons-material";
import axios from "axios";
import configs from "../../Constants";
import { toast } from "react-toastify";

const ProductOffers = ({ product, groupProducts, setShowOffers }) => {
    const [offerType, setOfferType] = useState("");
    const [valX, setValX] = useState("");
    const [valY, setValY] = useState("");
    const [valPct, setValPct] = useState("");
    const [offerPrice, setOfferPrice] = useState("");
    const [offerId, setOfferId] = useState(null);
    const [isUploading, setUploading] = useState(false);

    const baseURL = configs.baseURL;
    const merchantData = sessionStorage.getItem("merchantData")
        ? JSON.parse(sessionStorage.getItem("merchantData"))
        : null;
    const merchCode = merchantData ? merchantData.merchantCode : "";
    const userToken = sessionStorage.getItem("token") || "";

    useEffect(() => {
        const fetchOffer = async () => {
            if (groupProducts && groupProducts.length > 0) {
                // Group offer logic - maybe fetch existing group offer if editing?
                // For now, assuming new group offer or handled via edit click passing relevant info
                // If editing, offerId would be helpful to pass in.
                // Assuming we just use default/empty state for new group offer for now.
            } else if (product && product.id) {
                try {
                    const response = await axios.get(
                        `${baseURL}/api/offers/product/${product.id}?merchantCode=${merchCode}`,
                        { headers: { Authorization: `Bearer ${userToken}` } }
                    );
                    const data = response.data;
                    const offer = Array.isArray(data) ? data[0] : (data && (data.id || data._id) ? data : null);

                    if (offer) {
                        setOfferId(offer.id || offer._id);
                        setOfferType(offer.type || "");
                        setValX(offer.X !== undefined ? offer.X : 0);

                        const YVal = offer.Y !== undefined ? offer.Y : 0;
                        if (offer.type === "BUY X GET % OFF" || offer.type === "Flat % OFF" || offer.offer_type === "BUY X GET % OFF" || offer.offer_type === "Flat % OFF") {
                            setValPct(YVal);
                            setValY("");
                        } else {
                            setValY(YVal);
                            setValPct("");
                        }
                        setOfferPrice(offer.overridePrice || 0);
                    } else if (product) {
                        // Fallback to product fields if no independent offer record found
                        setOfferType(product.type || "");
                        setValX(product.X !== undefined ? product.X : "");
                        setValY(product.Y !== undefined ? product.Y : "");
                        setValPct(product.offer_pct || "");
                        setOfferPrice(product.overridePrice || "");
                    }
                } catch (error) {
                    console.error("Error fetching offer:", error);
                    // Fallback to product fields on error
                    setOfferType(product.type || "");
                    setValX(product.X || "");
                    setValY(product.Y || "");
                    setOfferPrice(product.overridePrice || "");
                }
            }
        };
        fetchOffer();
    }, [product, baseURL, merchCode, userToken]);

    const handleSave = async () => {
        setUploading(true);
        try {
            const payload = {
                type: offerType,
                X: parseInt(valX) || 0,
                Y: (offerType === "BUY X GET % OFF" || offerType === "Flat % OFF") ? (parseInt(valPct) || 0) : (offerType === "BUY X FOR FLAT Y" ? (parseFloat(valY) || 0) : (parseInt(valY) || 0)),
                overridePrice: offerPrice,
                repeatable: true,
                merchantCode: merchCode
            };

            if (groupProducts && groupProducts.length > 0) {
                payload.productIds = groupProducts.map(p => p.id);
            } else {
                payload.productId = product.id;
            }

            if (offerId) {
                await axios.put(
                    `${baseURL}/api/offers/${offerId}?merchantCode=${merchCode}`,
                    payload,
                    { headers: { Authorization: `Bearer ${userToken}` } }
                );
            } else {
                await axios.post(
                    `${baseURL}/api/offers?merchantCode=${merchCode}`,
                    payload,
                    { headers: { Authorization: `Bearer ${userToken}` } }
                );
            }
            toast.success("Offer saved successfully!");
            setShowOffers(false);
        } catch (error) {
            console.error("Error saving offer:", error);
            toast.error("Failed to save offer.");
        } finally {
            setUploading(false);
        }
    };

    return (
        <Dialog
            open={true}
            maxWidth="sm"
            onClose={() => setShowOffers(false)}
            fullWidth={true}
        >
            <DialogTitle style={{ fontWeight: "bold" }}>
                {groupProducts && groupProducts.length > 0
                    ? `Add Group Offer`
                    : `Add Offers for ${product?.name}`}
            </DialogTitle>
            <IconButton
                aria-label="close"
                onClick={() => setShowOffers(false)}
                sx={{
                    position: "absolute",
                    right: 8,
                    top: 8,
                    color: (theme) => theme.palette.grey[500],
                }}
            >
                <Close />
            </IconButton>
            <DialogContent dividers>
                <div style={{ display: "flex", flexDirection: "column", gap: "20px", padding: "10px" }}>
                    <FormControl fullWidth>
                        <InputLabel id="offer-type-label">Offer Type</InputLabel>
                        <Select
                            labelId="offer-type-label"
                            id="offer-type-select"
                            value={offerType}
                            label="Offer Type"
                            onChange={(e) => setOfferType(e.target.value)}
                        >
                            <MenuItem value=""><em>None</em></MenuItem>
                            <MenuItem value="BUY X GET Y FREE">BUY X GET Y FREE</MenuItem>
                            <MenuItem value="BUY X GET % OFF">BUY X GET % OFF</MenuItem>
                            <MenuItem value="Flat % OFF">Flat % OFF</MenuItem>
                            <MenuItem value="BUY X FOR FLAT Y">BUY X FOR FLAT Y</MenuItem>
                        </Select>
                    </FormControl>

                    {offerType === "BUY X GET Y FREE" && (
                        <div style={{ display: "flex", gap: "10px" }}>
                            <TextField
                                fullWidth
                                type="number"
                                label="Buy (X)"
                                value={valX}
                                onChange={(e) => setValX(e.target.value)}
                            />
                            <TextField
                                fullWidth
                                type="number"
                                label="Get (Y) Free"
                                value={valY}
                                onChange={(e) => setValY(e.target.value)}
                            />
                        </div>
                    )}

                    {offerType === "BUY X FOR FLAT Y" && (
                        <div style={{ display: "flex", gap: "10px" }}>
                            <TextField
                                fullWidth
                                type="number"
                                label="Buy (X)"
                                value={valX}
                                onChange={(e) => setValX(e.target.value)}
                            />
                            <TextField
                                fullWidth
                                type="number"
                                label="Flat Price (Y)"
                                value={valY}
                                onChange={(e) => setValY(e.target.value)}
                            />
                        </div>
                    )}

                    {offerType === "BUY X GET % OFF" && (
                        <div style={{ display: "flex", gap: "10px" }}>
                            <TextField
                                fullWidth
                                type="number"
                                label="Buy (X)"
                                value={valX}
                                onChange={(e) => setValX(e.target.value)}
                            />
                            <TextField
                                fullWidth
                                type="number"
                                label="Discount %"
                                value={valPct}
                                onChange={(e) => setValPct(e.target.value)}
                            />
                        </div>
                    )}

                    {offerType === "Flat % OFF" && (
                        <TextField
                            fullWidth
                            type="number"
                            label="Flat Discount %"
                            value={valPct}
                            onChange={(e) => setValPct(e.target.value)}
                        />
                    )}

                    <TextField
                        fullWidth
                        type="number"
                        label="Override Offer Price (Optional)"
                        placeholder="Discounted price"
                        value={offerPrice}
                        onChange={(e) => setOfferPrice(e.target.value)}
                    />
                </div>
            </DialogContent>
            <DialogActions>
                <Button color="error" onClick={() => setShowOffers(false)}>
                    Close
                </Button>
                <Button
                    variant="contained"
                    color="success"
                    disabled={isUploading}
                    onClick={handleSave}
                >
                    Save Offer
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default ProductOffers;
